import PageBreadcrumb from 'components/common/PageBreadcrumb';
import React, { useState } from 'react';
import { breadcrumbItems } from './config';
import Button from 'components/base/Button';
import { Link, useNavigate } from 'react-router-dom';
import UploadPreview from './uploadAndPreview/UploadPreview';
import { JsonData } from './bulkStudentUpload.type';
import * as Yup from 'yup';
import {
  useFindCandidateRegisterNumberQuery,
  useSaveBulkCandidateMutation
} from 'service/institutionQuery/institutionQuery';

function BulkStudentUpload() {
  const [jsonData, setJsonData] = useState<JsonData>([]);
  const [errors, setErrors] = useState<string[] | null>(null);

  const { data: existingData } = useFindCandidateRegisterNumberQuery('');
  const [saveBulkCandidate, { isLoading }] = useSaveBulkCandidateMutation();

  const navigate = useNavigate();

  const resetError = () => setErrors(null);

  const validationSchema = Yup.array().of(
    Yup.object({
      username: Yup.string()
        .required('Username is required')
        .min(3, 'Username must be at least 3 characters')
        .max(20, 'Username cannot exceed 20 characters'),

      registerNumber: Yup.string()
        .required('Register Number is required')
        .matches(/^[0-9]+$/, 'Register Number must be numeric')
        .length(10, 'Register Number must be exactly 10 digits')
        .test(
          'unique-registerNumber',
          'Register Number must be unique',
          value => {
            const matchedNumber = Array.isArray(jsonData)
              ? [
                  ...jsonData,
                  ...(Array.isArray(existingData) ? existingData : [])
                ].filter(({ registerNumber }) => registerNumber === value)
              : null;
            console.log(matchedNumber, 'matchedNumber');

            return Array.isArray(jsonData)
              ? [
                  ...jsonData,
                  ...(Array.isArray(existingData) ? existingData : [])
                ].filter(({ registerNumber }) => registerNumber === value)
                  .length === 1
              : false;
          }
        ),

      gender: Yup.string()
        .required('Gender is required')
        .oneOf(
          ['male', 'female', 'other'],
          'Gender must be male, female, or other'
        ),

      email: Yup.string()
        .required('Email is required')
        .email('Enter a valid email address')
        .test('unique-email', 'Email Address must be unique', value => {
          return Array.isArray(jsonData)
            ? [
                ...jsonData,
                ...(Array.isArray(existingData) ? existingData : [])
              ].filter(({ email }) => email === value).length === 1
            : false;
        }),

      contactNumber: Yup.string()
        .required('Contact Number is required')
        .matches(/^[0-9]+$/, 'Contact Number must be numeric')
        .min(10, 'Contact Number must be at least 10 digits')
        .max(15, 'Contact Number cannot exceed 15 digits'),

      dateOfBirth: Yup.date()
        .required('Date of Birth is required')
        .max(new Date(), 'Date of Birth cannot be in the future')
    })
  );

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(jsonData, { abortEarly: false });
      resetError();
      saveBulkCandidate(jsonData)
        .unwrap()
        .then(() => {
          navigate('/hub/students');
        })
        .catch(console.error);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errorMessages: string[] = [];
        err.inner.forEach((error: Yup.ValidationError) => {
          const message = `*${error.message}*`;
          if (!errorMessages.includes(message)) {
            errorMessages.push(message);
          }
        });
        setErrors(errorMessages);
      }
    }
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <section className="mb-9">
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">Bulk Student Upload</h2>
            <h5 className="text-body-tertiary fw-semibold">
              Create a group of students in a single step.
            </h5>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button variant="phoenix-secondary" as={Link} to="/hub/students">
              Back
            </Button>
            <Button
              variant="primary"
              disabled={
                !(Array.isArray(jsonData) && jsonData.length > 0) || isLoading
              }
              onClick={handleSubmit}
              loading={isLoading}
              loadingPosition="start"
            >
              Upload
            </Button>
          </div>
          {errors && (
            <div className="invalid-feedback d-block">{errors.join('')}</div>
          )}
        </div>
        <div className="mb-5">
          <p className="text-body-tertiary fw-semibold">
            Download the provided CSV template, enter the required student
            details, and re-upload to seamlessly add multiple students in bulk.
          </p>
          <div>
            <a
              className="btn btn-outline-secondary"
              download
              href="/doc/sample.csv"
            >
              Download
            </a>
            <small className="text-body-tertiary mx-2">sample.csv</small>
          </div>
        </div>
        <UploadPreview
          jsonData={jsonData}
          setJsonData={setJsonData}
          resetError={resetError}
        />
      </section>
    </div>
  );
}

export default BulkStudentUpload;
